import { useContext, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { StyleContext } from "../../providers/StyleContextProvider";
import { AuthContext } from "../../providers/AuthContextProvider";
import { LoggedAdmin } from "../../models/LoggedAdmin";
import { Option } from "../../models/Option";
import { ResponseModel } from "../../models/ResponseModel";
import { AdminVideoLesson, VideoLessonsCount } from "../../models/VideoLesson";
import { approveVideoLesson, deleteVideoLesson, getVideoLessons, getVideoLessonsCount, getVideoLessonTeachers } from "../../services/VideoLessonService";
import { getFormatedDate } from "../../helpers/Common";
import useErrorPage from "../../hooks/useErrorPage";
import useAdminLoginRedirect from "../../hooks/useAdminLoginRedirect";
import { useAlert } from "../../hooks/useAlert";
import { Header } from "../../components/shared/Header";
import { Button } from "../../components/shared/Button";
import { Select } from "../../components/shared/Select";
import { NoData } from "../../components/shared/NoData";
import { Pagination } from "../../components/shared/Pagination";
import { PopUpDialog } from "../../components/shared/dialogs/PopUpDialog";
import { TableLoader } from "../../components/loaders/TableLoader";
import { SelectLoader } from "../../components/loaders/SelectLoader";
import { ReactComponent as Edit } from "../../assets/svgs/edit.svg";
import { ReactComponent as Delete } from "../../assets/svgs/bin.svg";
import { ReactComponent as Approve } from "../../assets/svgs/check-circle.svg";

const PAGE_SIZE = 40;

// Pending is the review queue, so it is the default tab
const STATUS_TABS = [
    { status: "PENDING", label: "Pending" },
    { status: "APPROVED", label: "Approved" },
    { status: "ALL", label: "All" }
];

export const VideoLessonListPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const styleContext = useContext(StyleContext);
    const authContext = useContext(AuthContext);
    const [styles, setStyles] = useState<any>(styleContext.getComponentStyle("videoLessonListPage"));
    const admin: LoggedAdmin | null = authContext.getLoggedAdmin();
    const page: number = searchParams.get("page") === null ? 1 : +searchParams.get("page")!;
    const status: string = searchParams.get("status") ?? "PENDING";
    const teacherId: number | undefined = searchParams.get("teacherId") === null ? undefined : +searchParams.get("teacherId")!;
    const [teachers, setTeachers] = useState<Option[]>();
    const [lessonsCount, setLessonsCount] = useState<VideoLessonsCount>();
    const [lessons, setLessons] = useState<AdminVideoLesson[]>();
    const [deletingLesson, setDeletingLesson] = useState<AdminVideoLesson | null>(null);
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const [toErrorPage] = useErrorPage();
    const [toAdminLogin] = useAdminLoginRedirect();
    const { Alert, openAlert } = useAlert();

    useEffect(() => {
        if (admin === null) {
            // Callback url defaults to the current path, so the admin returns to this list (with its filters) after login
            toAdminLogin("Please login to review video lessons.");
            return;
        }
        let active = true;
        (async () => {
            try {
                const teacherList: Option[] = await getVideoLessonTeachers(admin);
                if (active) setTeachers(teacherList);
            } catch (error) {
                if (active) toErrorPage(error);
            }
        })();
        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (admin === null) return;
        // Ignore a slow response once the filters have changed again
        let active = true;
        setLessons(undefined);
        loadLessons(() => active);
        window.scrollTo(0, 0);
        return () => { active = false; };
    }, [page, status, teacherId]);

    useEffect(() => {
        setStyles(styleContext.getComponentStyle("videoLessonListPage"));
    }, [isMobile]);

    async function loadLessons(isActive: () => boolean = () => true) {
        try {
            const [lessonList, counts]: [AdminVideoLesson[], VideoLessonsCount] = await Promise.all([
                getVideoLessons(admin!, page, PAGE_SIZE, status, teacherId),
                getVideoLessonsCount(admin!, teacherId)
            ]);
            if (isActive()) {
                setLessons(lessonList);
                setLessonsCount(counts);
            }
        } catch (error) {
            if (isActive()) toErrorPage(error);
        }
    }

    // Filters live in the url so refresh and back keep the admin's place
    const updateSearchParam = (name: string, value?: string) => {
        if (value) {
            searchParams.set(name, value);
        }
        else {
            searchParams.delete(name);
        }
        if (name !== "page") {
            searchParams.delete("page");
        }
        navigate(`?${searchParams}`);
    }

    const approveLesson = async (lesson: AdminVideoLesson) => {
        try {
            const response: ResponseModel = await approveVideoLesson(admin!, lesson.videoLessonId);
            if (response.status) {
                openAlert("success", `${lesson.lessonName} is approved and now visible to students.`);
                await loadLessons();
            }
            else {
                openAlert("error", response.message || "Oops! something went wrong.");
            }
        } catch (error) {
            toErrorPage(error);
        }
    }

    const onDeleteClick = (lesson: AdminVideoLesson) => {
        // The API blocks deleting purchased lessons; say so up front instead of after a confirm
        if (lesson.hasPurchases) {
            openAlert("error", "This lesson has been purchased, so it cannot be deleted.");
            return;
        }
        setDeletingLesson(lesson);
    }

    const deleteLesson = async () => {
        const lesson = deletingLesson!;
        setDeletingLesson(null);
        try {
            const response: ResponseModel = await deleteVideoLesson(admin!, lesson.videoLessonId);
            if (response.status) {
                openAlert("success", `${lesson.lessonName} has been deleted.`);
                await loadLessons();
            }
            else {
                openAlert("error", response.message || "Oops! something went wrong.");
            }
        } catch (error) {
            toErrorPage(error);
        }
    }

    const getTabCount = (tabStatus: string): string => {
        if (!lessonsCount) return "";
        if (tabStatus === "PENDING") {
            return lessonsCount.editedCount > 0
                ? `${lessonsCount.pendingCount} (${lessonsCount.editedCount} edited)`
                : `${lessonsCount.pendingCount}`;
        }
        return `${tabStatus === "APPROVED" ? lessonsCount.approvedCount : lessonsCount.allCount}`;
    }

    if (admin === null) return null;

    const totalPages = lessons && lessons.length > 0 ? Math.ceil(lessons[0].recordCount / PAGE_SIZE) : 0;

    return (
        <>
            <Header />
            <div style={styles}>
                <h1 style={styles.heading}>{"Video Lessons"}</h1>
                <div style={styles.toolbar}>
                    {STATUS_TABS.map((tab) =>
                        <Button
                            key={tab.status}
                            style={{...styles.statusButton, ...(status === tab.status && styles.statusButton.active)}}
                            onClick={() => updateSearchParam("status", tab.status)}
                        >
                            {`${tab.label} ${getTabCount(tab.status)}`}
                        </Button>
                    )}
                    {teachers === undefined ? <SelectLoader count={1} /> :
                    <Select
                        styles={styles.teacherSelect}
                        label={"Teachers"}
                        options={teachers}
                        initialValue={teacherId?.toString()}
                        getSelectedValue={(value?: number) => updateSearchParam("teacherId", value?.toString())}
                    />}
                    <Button style={styles.payoutsButton} onClick={() => navigate('/video-lessons/payouts')}>{"Payouts"}</Button>
                    <Button style={styles.newButton} onClick={() => navigate('/video-lessons/edit/0')}>{"New Lesson"}</Button>
                </div>
                {lessons === undefined ? <TableLoader columnCount={7} rowCount={8} /> :
                lessons.length === 0 ? <NoData message={status === "PENDING" ? "No lessons are waiting for review." : undefined} /> :
                <>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.table.header}>
                                <th style={styles.table.idColumn}>{"Id"}</th>
                                <th style={styles.table.imageColumn}>{"Image"}</th>
                                <th style={styles.table.lessonColumn}>{"Lesson"}</th>
                                <th style={styles.table.teacherColumn}>{"Teacher & Subject"}</th>
                                <th style={styles.table.contentColumn}>{"Content"}</th>
                                <th style={styles.table.statusColumn}>{"Status"}</th>
                                <th style={styles.table.actionColumn}>{"Action"}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {lessons.map((lesson) =>
                                <LessonRow
                                    key={lesson.videoLessonId}
                                    lesson={lesson}
                                    styles={styles.table}
                                    onEdit={() => navigate('/video-lessons/edit/' + lesson.videoLessonId)}
                                    onApprove={() => approveLesson(lesson)}
                                    onDelete={() => onDeleteClick(lesson)}
                                />
                            )}
                        </tbody>
                    </table>
                    {totalPages > 1 && <Pagination page={page} totalPages={totalPages} handlePagination={(pageNo) => updateSearchParam("page", pageNo.toString())} />}
                </>}
            </div>
            <PopUpDialog styles={styles.deleteDialog} open={deletingLesson !== null} toggle={() => setDeletingLesson(null)}>
                <div style={styles.deleteDialog.text}>{`${deletingLesson?.lessonName} will be deleted permanently.`}</div>
                <Button style={styles.deleteDialog.button} type="button" onClick={deleteLesson}>{"Delete"}</Button>
            </PopUpDialog>
            <Alert />
        </>
    )
}

interface LessonRowProps {
    lesson: AdminVideoLesson;
    styles: any;
    onEdit(): void;
    onApprove(): void;
    onDelete(): void;
}

const LessonRow: React.FC<LessonRowProps> = ({ lesson, styles, onEdit, onApprove, onDelete }) => (
    <tr style={styles.tr}>
        <td>{lesson.videoLessonId}</td>
        <td>{lesson.lessonImageUrl && <img style={styles.image} src={lesson.lessonImageUrl} alt="lesson" />}</td>
        <td style={styles.leftCell}>
            <div style={styles.primaryText}>{lesson.lessonName}</div>
            <div>{lesson.paymentRequired ? `LKR ${lesson.price ?? 0}` : "Free"}</div>
        </td>
        <td style={styles.leftCell}>
            <div style={styles.primaryText}>{lesson.teacherAccountName}</div>
            <div>{lesson.whatsappNo}</div>
            {/* The name shown to students can differ from the account name; show it so the reviewer can check it */}
            {lesson.teacherName && lesson.teacherName !== lesson.teacherAccountName && <div style={styles.mutedText}>{`Shown as: ${lesson.teacherName}`}</div>}
            <div style={styles.subjectText}>{lesson.subjects}</div>
            <div style={styles.mutedText}>{lesson.grades}</div>
        </td>
        <td style={styles.leftCell}>
            <div style={lesson.hasVideo ? styles.okText : styles.warningText}>{lesson.hasVideo ? "Video uploaded" : "No video"}</div>
            <div style={styles.mutedText}>{lesson.hasTute ? "Tute available" : "No tute"}</div>
        </td>
        <td style={styles.leftCell}>
            <LessonStatus lesson={lesson} styles={styles} />
        </td>
        <td>
            <div style={styles.actionWrapper}>
                <span style={styles.action} onClick={onEdit}><Edit style={{...styles.icon, ...styles.icon.edit}} />{"Edit"}</span>
                {lesson.pending && <span style={styles.action} onClick={onApprove}><Approve style={{...styles.icon, ...styles.icon.approve}} />{"Approve"}</span>}
                <span style={{...styles.action, ...(lesson.hasPurchases && styles.action.locked)}} onClick={onDelete}><Delete style={{...styles.icon, ...styles.icon.delete}} />{"Delete"}</span>
            </div>
        </td>
    </tr>
)

// A pending lesson with an approved date is an edit of a lesson that was live before
const LessonStatus: React.FC<{ lesson: AdminVideoLesson, styles: any }> = ({ lesson, styles }) => {
    if (lesson.pending) {
        return (
            <>
                <div style={lesson.approvedDate ? styles.editedTag : styles.newTag}>{lesson.approvedDate ? "Edited" : "New"}</div>
                <div style={styles.mutedText}>{`Submitted: ${getFormatedDate(lesson.updatedDate ?? lesson.createdDate!)}`}</div>
                {lesson.approvedDate && <div style={styles.mutedText}>{`Last approved: ${getFormatedDate(lesson.approvedDate)}`}</div>}
            </>
        );
    }
    return (
        <>
            <div style={styles.approvedTag}>{"Approved"}</div>
            {lesson.approvedDate && <div style={styles.mutedText}>{`On: ${getFormatedDate(lesson.approvedDate)}`}</div>}
            {lesson.approvedByName && <div style={styles.mutedText}>{`By: ${lesson.approvedByName}`}</div>}
        </>
    );
}
